import { useState, useMemo } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { Users, X, Plus, Code2, LayoutGrid, Handshake, Heart, UserCog, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { RoleNames } from '@/lib/permissions'
import { MemberStatusBadge } from '@/components/tenant-members/MemberStatusBadge'
import { useAllUsers, useRoles } from '@/hooks'
import type { CreateProjectRequest, TenantMemberStatus } from '@/types'

export interface FormTeamMember {
  userId: string
  roleIds: string[]
  displayName: string
  email: string
  status: TenantMemberStatus
}

export interface ExtendedCreateProjectRequest extends Omit<CreateProjectRequest, 'teamMembers'> {
  teamMembers: FormTeamMember[]
}

const getRoleIcon = (roleName: string, className: string = "h-4 w-4") => {
  switch (roleName) {
    case RoleNames.Developer:
      return <Code2 className={className} />
    case RoleNames.QualityAssurance:
      return <LayoutGrid className={className} />
    case RoleNames.ProductOwner:
      return <Handshake className={className} />
    case RoleNames.Guest:
      return <Heart className={className} />
    case RoleNames.Admin:
      return <UserCog className={className} />
    default:
      return <Code2 className={className} />
  }
}

export function TeamAssignmentSection() {
  const { control, formState: { errors }, clearErrors } = useFormContext<ExtendedCreateProjectRequest>()
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'teamMembers',
  })

  const { data: users = [], isLoading: usersLoading } = useAllUsers()
  const { data: roles = [], isLoading: rolesLoading } = useRoles()

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([])
  const [userPopoverOpen, setUserPopoverOpen] = useState(false)
  const [userSearch, setUserSearch] = useState('')

  const nonAdminRoles = useMemo(() => roles.filter(r => r.name !== RoleNames.Admin), [roles])

  const roleOptions = useMemo(() => nonAdminRoles.map(r => ({
    value: r.id,
    label: r.name,
    icon: getRoleIcon(r.name),
  })), [nonAdminRoles])

  const availableUsers = useMemo(() => {
    const assignedUserIds = new Set(fields.map(f => f.userId))
    return users.filter(user => !assignedUserIds.has(user.id))
  }, [users, fields])

  const selectedUsers = useMemo(() =>
    users.filter(u => selectedUserIds.includes(u.id)),
  [users, selectedUserIds])

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleRemoveSelectedUser = (userId: string) => {
    setSelectedUserIds(prev => prev.filter(id => id !== userId))
  }

  const filteredAvailableUsers = useMemo(() => {
    if (!userSearch.trim()) return availableUsers
    const search = userSearch.toLowerCase()
    return availableUsers.filter(
      user => user.displayName.toLowerCase().includes(search) ||
              user.email.toLowerCase().includes(search)
    )
  }, [availableUsers, userSearch])

  const handleAddMembers = () => {
    if (selectedUserIds.length === 0 || selectedRoleIds.length === 0) return

    const usersToAdd = users.filter(u => selectedUserIds.includes(u.id))

    for (const user of usersToAdd) {
      append({
        userId: user.id,
        roleIds: selectedRoleIds,
        displayName: user.displayName,
        email: user.email,
        status: user.status,
      })
    }

    clearErrors('teamMembers')

    setSelectedUserIds([])
    setSelectedRoleIds([])
  }

  const handleRoleChange = (index: number, newRoleIds: string[]) => {
    const member = fields[index]
    update(index, {
      ...member,
      roleIds: newRoleIds,
    })
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const getMemberLabel = (member: { displayName: string; email: string }) =>
    member.displayName || member.email

  if (usersLoading || rolesLoading) {
    return (
      <div className="space-y-4">
        <Label className="text-base font-semibold">Assign Team</Label>
        <div role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Label className="text-lg font-semibold block">Assign Team <span className="text-red-500">*</span></Label>

      <div className="space-y-2">
        <Label className="text-sm">Team Members <span className="text-red-500">*</span></Label>

        <div className="flex gap-3 items-stretch">
          <div className="flex-1 flex border rounded-md min-h-[42px]">
            <div className="flex-1 flex flex-wrap items-center gap-2 p-2">
              <Users className="h-5 w-5 text-muted-foreground shrink-0" />
              {selectedUsers.map(user => (
                <Badge key={user.id} variant="secondary" className="gap-1 pr-1">
                  {getMemberLabel(user)}
                  <button
                    type="button"
                    onClick={() => handleRemoveSelectedUser(user.id)}
                    className="ml-1 hover:bg-muted rounded-full"
                    aria-label={`Remove ${getMemberLabel(user)}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <DropdownMenu open={userPopoverOpen} onOpenChange={(open) => {
                setUserPopoverOpen(open)
                if (!open) setUserSearch('')
              }}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    Add more
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-72 p-2" align="start">
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="mb-2"
                    onKeyDown={(e) => e.stopPropagation()}
                    aria-label="Search users"
                  />
                  <div className="max-h-48 overflow-y-auto">
                    {filteredAvailableUsers.length === 0 ? (
                      <div className="text-sm text-muted-foreground text-center py-2">
                        No users found.
                      </div>
                    ) : (
                      filteredAvailableUsers.map(user => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleToggleUser(user.id)}
                          aria-pressed={selectedUserIds.includes(user.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded text-left"
                        >
                          <div className={cn(
                            "h-4 w-4 border rounded flex items-center justify-center shrink-0",
                            selectedUserIds.includes(user.id) ? "bg-primary border-primary" : "border-border"
                          )}>
                            {selectedUserIds.includes(user.id) && (
                              <Check className="h-3 w-3 text-primary-foreground" />
                            )}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm truncate">{getMemberLabel(user)}</span>
                            {user.displayName && (
                              <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="w-px bg-border self-stretch my-2" />

            <div className="w-48 p-1">
              <MultiSelect
                options={roleOptions}
                value={selectedRoleIds}
                onChange={setSelectedRoleIds}
                placeholder="Select roles..."
                showIconsInTrigger
                displayMode="compact"
                className="border-0 shadow-none"
              />
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleAddMembers}
            disabled={selectedUserIds.length === 0 || selectedRoleIds.length === 0}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {fields.length > 0 && (
        <div className="border rounded-lg divide-y">
          {fields.map((member, index) => (
            <div key={member.id} data-testid="assigned-team-member" className="flex items-center gap-4 p-4">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary/10 text-primary">
                  {getInitials(getMemberLabel(member))}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{getMemberLabel(member)}</div>
                {member.displayName && (
                  <div className="text-sm text-muted-foreground truncate">{member.email}</div>
                )}
              </div>

              <MemberStatusBadge status={member.status} />

              <div className="w-64">
                <MultiSelect
                  options={roleOptions}
                  value={member.roleIds}
                  onChange={(newRoleIds) => handleRoleChange(index, newRoleIds)}
                  placeholder="Select roles..."
                  displayMode="compact"
                />
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => remove(index)}
                className="text-muted-foreground"
                aria-label={`Remove ${getMemberLabel(member)} from team`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {fields.length === 0 && (
        <div className="text-sm text-muted-foreground py-4 text-center border rounded-lg bg-muted/40">
          No team members assigned yet. Add at least one team member.
        </div>
      )}

      {errors.teamMembers && (
        <p role="alert" className="text-sm text-destructive">
          {typeof errors.teamMembers.message === 'string'
            ? errors.teamMembers.message
            : 'At least one team member is required'}
        </p>
      )}
    </div>
  )
}
